import { Badge, Box, Flex, Menu, Text, theme } from '@/components';
import { datetimeFormat } from '@/utils';
import type { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { TopMenuItem } from '../_utils/side-menu';

type MenuProps = ComponentProps<typeof Menu>;

type Props = {
  selectedKey?: string;
  collapsed: boolean;
  topMenu: TopMenuItem[];
  bottomMenu: MenuProps['items'];
  secondLastLoginTime?: string | null;
};

const NotificationBadge: FC<{
  count: number | undefined;
  offset?: [number, number];
  color?: string;
}> = ({ count, offset, color }) => (
  <Badge
    dot={count === undefined}
    offset={offset ?? undefined}
    count={count}
    size="small"
    style={{ boxShadow: 'none' }}
    color={color}
  />
);

const NotificationCountBadge = ({
  count,
  position = 'top',
  color = 'red',
}: {
  count?: number;
  position?: 'top' | 'bottom';
  color?: string;
}) => (
  <Box
    display="flex"
    position="absolute"
    right={count && count < 10 ? 4 : 0}
    {...(position === 'top' ? { top: 4 } : { bottom: 4 })}
  >
    <NotificationBadge count={count} color={color} />
  </Box>
);

const NotificationIcon = ({
  item,
  collapsed,
}: {
  item: TopMenuItem;
  collapsed: boolean;
}) => (
  <>
    {item.icon}
    {collapsed && (
      <>
        {item.isShowNotificationBadge && (
          <NotificationCountBadge
            color={item.notificationBadgeColor}
            count={item.notificationBadgeCount}
          />
        )}
        {item.isShowNotificationBadgeBottom && (
          <NotificationCountBadge
            count={item.notificationBadgeCountBottom}
            position="bottom"
            color={item.notificationBadgeColor ?? 'blue'}
          />
        )}
      </>
    )}
  </>
);

const NotificationLabel = ({
  item,
  collapsed,
}: {
  item: TopMenuItem;
  collapsed: boolean;
}) => {
  return (
    <Link to={item.path}>
      {!collapsed && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {item.label}
          {item.isShowNotificationBadge && (
            <NotificationCountBadge
              color={item.notificationBadgeColor}
              count={item.notificationBadgeCount}
            />
          )}
          {item.isShowNotificationBadgeBottom && (
            <NotificationCountBadge
              count={item.notificationBadgeCountBottom}
              position="bottom"
              color={item.notificationBadgeColor ?? 'blue'}
            />
          )}
        </Box>
      )}
    </Link>
  );
};

const convertMenuItem = (item: TopMenuItem, collapsed: boolean) => ({
  key: item.key,
  title: item.label,
  icon: <NotificationIcon item={item} collapsed={collapsed} />,
  label: <NotificationLabel item={item} collapsed={collapsed} />,
});

export const SidebarMenu: FC<Props> = ({
  selectedKey,
  collapsed,
  topMenu,
  bottomMenu,
  secondLastLoginTime,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const borderStyle = `1px solid ${token.colorFill}`;
  const selectedKeys = selectedKey ? [selectedKey] : [];

  return (
    <Flex vertical style={{ flex: 1 }}>
      <Box borderTop={borderStyle} overflowY="auto" flexGrow={1} flexBasis={0}>
        <Menu
          selectedKeys={selectedKeys}
          style={{ borderRight: 0 }}
          mode="inline"
          theme="light"
          items={topMenu.map((item) => convertMenuItem(item, collapsed))}
        />
      </Box>
      <Box borderTop={borderStyle}>
        <Menu
          selectedKeys={selectedKeys}
          style={{ marginBottom: 'auto', borderRight: 0 }}
          mode="inline"
          theme="light"
          items={bottomMenu}
        />
        {!collapsed && (
          <Box padding="0 16px">
            <Text
              text={`[${t('last-login-time')}]`}
              variant="small"
              style={{
                color: token.colorTextSecondary,
                paddingRight: '4px',
              }}
            />
            <Text
              text={
                secondLastLoginTime
                  ? datetimeFormat({
                      datetime: secondLastLoginTime,
                    })
                  : undefined
              }
              variant="small"
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
};
